// import node module libraries
import { Fragment, useEffect, useState } from 'react';
import { Alert, Col, Row, Breadcrumb, Card, Form, Offcanvas, Button, Spinner } from 'react-bootstrap';
import { ExclamationCircleFill, CheckCircleFill, ExclamationTriangleFill } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import axios from 'axios';

// Import Loading Animation
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

// import custom components
import { FormSelect } from 'components/elements/form-select/FormSelect';

// import sub components
import FlowCard from 'components/marketing/pages/flows/FlowCard';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';

// Import translation
import { useTranslation } from 'react-i18next';

// import Create Configuration Modal
import ModalCreateConfiguration from './ModalCreateConfiguration';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

const Marketplace = () => {
	const { t } = useTranslation();
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });

	const auth = useAuthUser(); // Assuming this gets the auth user data

	// API-Call Options
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	// Flow Configs
	const [isLoadingFlowConfigs, setIsLoadingFlowConfigs] = useState(true);
	const [isFailedFlowConfigs, setIsFailedFlowConfigs] = useState(null);
	const [MyFlowConfigList, setFlowConfigList] = useState({
		data: []
	});

	// API-Call for all Data
	const getFlowConfigs = async () => {
		setIsLoadingFlowConfigs(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/tenants/${auth.tenant_id}/flows/configs`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				// Sort responseData by config_key before setting state
				const sortedData = [...responseData].sort((a, b) => {
					if (a.config_key < b.config_key) return -1;
					if (a.config_key > b.config_key) return 1;
					return 0;
				});
				setFlowConfigList({ data: sortedData });
				setIsLoadingFlowConfigs(false);
				setIsFailedFlowConfigs(null);
			})
			.catch((error) => {
				setIsLoadingFlowConfigs(false);
				setIsFailedFlowConfigs(error);
			});
	};

	// Marketplace Flows
	const [MarketplaceFlowsList, setMarketplaceFlowsList] = useState([]);
	const [isLoadingMarketplaceFlows, setIsLoadingMarketplaceFlows] = useState(true);
	const [isFailedMarketplaceFlows, setIsFailedMarketplaceFlows] = useState(null);
	const [isLoadedMarketplaceFlows, setIsLoadedMarketplaceFlows] = useState(null); // used to check if the marketplace flows are loaded
	
	const getMarketplaceFlows = async () => {
		setIsLoadingMarketplaceFlows(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/public/marketplaceentries?key=flow`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setMarketplaceFlowsList(
					responseData
						? responseData.map(item => ({
							id: item.id,
							...item.marketplace_value
						}))
						: []
				); // Map the data to match the expected keys
				setIsLoadingMarketplaceFlows(false);
				setIsFailedMarketplaceFlows(null);
				setIsLoadedMarketplaceFlows(true);
			})
			.catch((error) => {
				setIsLoadingMarketplaceFlows(false);
				setIsFailedMarketplaceFlows(error);
			});
	}

	useEffect(() => {
		getMarketplaceFlows();
	}, []);

	// paging start
	const [pageNumber, setPageNumber] = useState(0);
	const CardsPerPage = 100;
	const pagesVisited = pageNumber * CardsPerPage;

	const displayCards = MarketplaceFlowsList.slice(
		pagesVisited,
		pagesVisited + CardsPerPage
	).map((item, index) => {
		return (
			<Col xxl={3} xl={6} lg={12} xs={12} className="mb-4" key={index}>
				<FlowCard item={item} />
			</Col>
		);
	});
	// end of paging

	// searching code started
	const [searchTerm, setSearchTerm] = useState('');

	const getSearchTerm = (event) => {
		let searchTerm = event.target.value;
		setSearchTerm(searchTerm);
		if (searchTerm !== '') {
			const newMarketplaceFlowsList = MarketplaceFlowsList.filter((item) => {
				return Object.values(item)
					.join(' ')
					.toLowerCase()
					.includes(searchTerm.toLowerCase());
			});
			setMarketplaceFlowsList(newMarketplaceFlowsList.slice(0, 500));
			setPageNumber(0);
		} else {
			setMarketplaceFlowsList(MarketplaceFlowsList.slice(0, 500));
		}
	};
	// end of searching code

	useEffect(() => {
		if (MarketplaceFlowsList.length > 0) {
			getFlowConfigs();
		}
	}, [isLoadedMarketplaceFlows]);


	// Create Configuration Modal
	const [selectedFlow, setSelectedFlow] = useState(null);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [isLoadingButtonCreateConfiguration, setIsLoadingButtonCreateConfiguration] = useState(false);

	const handleConfirmCreate = async (config) => {

		if (selectedFlow) {
			setIsLoadingButtonCreateConfiguration(true);

			const data = {
				config_key: 'flow.'+selectedFlow.id+'.1',
				config_value: {
					status: 'new',
					progress: 0,
					configuration_units: {}
				}
			};

			const baseURL = process.env.REACT_APP_ENV === 'development'
				? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
				: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

			await axios.post(`${baseURL}/tenants/${auth.tenant_id}/flows/configs`, data, options)
				.then(response => {
					setIsLoadingButtonCreateConfiguration(false);
					setShowCreateModal(false);
					setSelectedFlow(null);
					window.scrollTo({ top: 0, behavior: 'smooth' });
					handleShowNotification(setNotification, 'success', t('Successful'), t('Configuration created successfully'));
					getFlowConfigs(); // Refresh the Flow Configs
				})
				.catch((error) => {
					setIsLoadingButtonCreateConfiguration(false);
					handleShowNotification(setNotification, 'danger', t('Error'), error.message);
				});
			
		}
	}

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<Row>
				<Col>
					<Row>
						<Col lg={12} md={12} sm={12}>
							<div className="border-bottom pb-4 mb-4 d-md-flex align-items-center justify-content-between">
								<div className="mb-3 mb-md-0">
									<h1 className="mb-1 h2 font-weight-bold">{t('Flows')}</h1>
									<Breadcrumb>
										<Breadcrumb.Item href="#">{t('Dashboard')}</Breadcrumb.Item>
										<Breadcrumb.Item href="#">{t('Marketplace')}</Breadcrumb.Item>
										<Breadcrumb.Item active>{t('Flows')}</Breadcrumb.Item>
									</Breadcrumb>
								</div>
							</div>
						</Col>
					</Row>
					{/* displaying records */}
					<h2 id="flows-in-marketplace" className="h3 font-weight-bold mt-4">{t('My Flows')}</h2>
					<Row className="justify-content-md-between mb-2">
						<Col xl={4} lg={4} md={6} xs={12}>
							<div className="mb-2 mb-lg-4">
								<p className="text-muted">{t('All your Flows in one place.')}</p>
							</div>
						</Col>
					</Row>
					<Row>
						{isLoadingFlowConfigs ? (
							<Row className="mb-5">
								<Col xs={3} className="text-center">
									<Skeleton className="rounded" count={1} height={450} />
								</Col>
								<Col xs={3} className="text-center">
									<Skeleton className="rounded" count={1} height={450} />
								</Col>
								<Col xs={3} className="text-center">
									<Skeleton className="rounded" count={1} height={450} />
								</Col>
								<Col xs={3} className="text-center">
									<Skeleton className="rounded" count={1} height={450} />
								</Col>
							</Row>		
						) : isFailedFlowConfigs ? (
							<Col className="text-center">
								<Alert variant="danger" className="bg-danger rounded">
									<ExclamationTriangleFill className="me-2" />
									{t('Error loading Flows')}
								</Alert>
							</Col>
						) : null}
						{/* Displaying Flow Cards */}
						{MyFlowConfigList.data.length > 0 && !isLoadingFlowConfigs && !isFailedFlowConfigs ? (
							<>
								{MyFlowConfigList.data.map((item, index) => {
									// Check if the Flow is available in the Marketplace
									// If not, set the status to "check" and show a message
									const matchedFlow = MarketplaceFlowsList.find(flow => 
										flow.id && item.config_key.includes(flow.id)
									);
									if(!matchedFlow) {
										item.status = "check";
										item.title = item.config_key;
										item.image = null;
										item.description = "This Flow is not available in the Marketplace anymore. Please contact us for more information.";
									}
									return (
										<Col xxl={3} xl={6} lg={12} xs={12} className="mb-4" key={index}>
											<FlowCard item={matchedFlow} config={item} viewby="config" showprogressbar />
										</Col>
									);
								})}
								</>
						) : null}
						{/* Empty card */}
						<Col xxl={3} xl={6} lg={12} xs={12} className="mb-4">
							<Card className="h-100 rounded border-light-secondary" style={{ border: '3px dashed', backgroundColor: 'transparent' }}>
								<Card.Body className="d-flex justify-content-center align-items-center">
									<Button variant="light-secondary" onClick={() => {
										const element = document.getElementById('flows-in-marketplace');
										if (element) {
											element.scrollIntoView({ behavior: 'smooth' });
										}
									}}>
										{t('Add new Flow')}
									</Button>
								</Card.Body>
							</Card>
						</Col>
						<>	
							<h2 id="flows-in-marketplace" className="h3 font-weight-bold mt-8">{t('Flows in Marketplace')}</h2>
							<Row className="justify-content-md-between mb-2">
								<Col xl={4} lg={4} md={6} xs={12}>	
									<div className="mb-2 mb-lg-4">
										<p className="text-muted">{t('All available Flows for your business. Your next automation is one Click & Buy away.')}</p>
									</div>
								</Col>
								<Col xxl={4} lg={4} md={6} xs={12}>
									{/* search records */}
									<Form.Control
										type="search"
										placeholder={t('Search Marketplace')}
										value={searchTerm}
										onChange={getSearchTerm}
									/>
								</Col>
							</Row>
							{isLoadingMarketplaceFlows ? (
								<Row className="mb-5">
									<Col xs={3} className="text-center">
										<Skeleton className="rounded" count={1} height={450} />
									</Col>
									<Col xs={3} className="text-center">
										<Skeleton className="rounded" count={1} height={450} />
									</Col>
									<Col xs={3} className="text-center">
										<Skeleton className="rounded" count={1} height={450} />
									</Col>
									<Col xs={3} className="text-center">
										<Skeleton className="rounded" count={1} height={450} />
									</Col>
								</Row>
							) : isFailedMarketplaceFlows ? (
								<Col className="text-center">
									<Alert variant="danger" className="bg-danger rounded">
										<ExclamationTriangleFill className="me-2" />
										{t('Error loading Flows')}
									</Alert>
								</Col>
							) : null}
							{/* Displaying Flow Cards */}
							{MarketplaceFlowsList.length > 0 && !isLoadingMarketplaceFlows && !isFailedMarketplaceFlows ? (
								MarketplaceFlowsList
									.map((item, index) => (
										<Col xxl={3} xl={6} lg={12} xs={12} className="mb-4" key={index}>
											<FlowCard
												item={item}
												viewby="marketplace"
												onClick={() => {
													setSelectedFlow(item); // Save flow to add in a state
													setShowCreateModal(true);
												}}
											/>
										</Col>
									))
							) : (
								!isLoadingMarketplaceFlows && (
									<Col className="text-center">
										<Alert variant="warning" className="bg-warning rounded">
											<ExclamationTriangleFill className="me-2" />
											{t('No Flows found in the marketplace')}
										</Alert>
									</Col>
								)
							)}
							<Col md={12} sm={12} className="mt-2">
								{/* Card*/}
								<Card>
									{/* Card body */}
									<Card.Body>
										<div className="d-lg-flex justify-content-between align-items-center">
											<h4 className="mb-0">{t('Are you missing a Flow?')}</h4>
											<span>
												{t('Let us know, please!')}{' '}
												<Link
													to="/marketing/pages/contact?subject=New%20Flow"
													target="_blank"
												>
													{t('Request a Flow here')}
													<i className="fe fe-arrow-up-right"></i>
												</Link>
											</span>
										</div>
									</Card.Body>
								</Card>
							</Col>
						</>
					</Row>
				</Col>
			</Row>
			{/* Create Configration Modal */}
			<ModalCreateConfiguration
				show={showCreateModal}
				onHide={() => setShowCreateModal(false)}
				selectedFlow={selectedFlow}
				handleConfirmCreate={handleConfirmCreate}
				isLoadingButton={isLoadingButtonCreateConfiguration}
			/>
		</Fragment>
	);

};

export default Marketplace;